/* eslint-disable i18next/no-literal-string */
import { isObject, type SimpleTradeCompanyListItem, useApiTradeV1TradeCompaniesList } from 'api';
import { useState } from 'react';
import type { AutocompleteProps } from 'ui';

import { AutocompleteField } from '../AutocompleteField/AutocompleteField';

type TradeCompanyAutocompleteProps = {
  name: string;
  label?: string;
  required?: boolean;
  onlyBuyers?: boolean;
  onlySellers?: boolean;
  excludeKycBanned?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  onSelectOption?: (value: SimpleTradeCompanyListItem) => void;
  defaultValue?: SimpleTradeCompanyListItem['id'];
} & Omit<AutocompleteProps<SimpleTradeCompanyListItem>, 'options' | 'defaultValue'>;

export const TradeCompanyAutocomplete = ({
  name,
  label = 'Select company',
  onlyBuyers = false,
  onlySellers = false,
  excludeKycBanned = false,
  size,
  required,
  onSelectOption,
  disabled = false,
  defaultValue,
  ...rest
}: TradeCompanyAutocompleteProps) => {
  const [query, setQuery] = useState(defaultValue?.toString() || '');

  const { data, isLoading } = useApiTradeV1TradeCompaniesList({
    exclude_kyc_banned: excludeKycBanned ? true : null,
    only_buyers: onlyBuyers ? 'true' : null,
    only_sellers: onlySellers ? 'true' : null,
    search: query,
  });

  return (
    <AutocompleteField<SimpleTradeCompanyListItem>
      options={data?.results || []}
      loading={isLoading}
      getOptionLabel={(item) => {
        if (isObject(item) && item?.name) {
          return item.name;
        }
        return '';
      }}
      label={label}
      name={name}
      isOptionEqualToValue={(option, value) => {
        return value === undefined || option?.id?.toString() === (value?.id ?? value)?.toString();
      }}
      placeholder="Type to search"
      valueField="id"
      size={size}
      required={required}
      data-testid="trade-company-autocomplete"
      onSelectOption={onSelectOption}
      disabled={disabled}
      filterOptions={(options, state) => {
        setQuery(state.inputValue);
        return options;
      }}
      {...rest}
    />
  );
};
